// Contact information detection utility
// Prevents users from sharing phone numbers, emails, and other contact details

export interface ContactInfoDetection {
  isValid: boolean;
  detectedType?: string;
  flaggedContent?: string;
  message?: string;
}

// Phone number patterns - various formats
const PHONE_PATTERNS = [
  // XXX-XXX-XXXX format (US phone numbers with dashes)
  /\b\d{3}-\d{3}-\d{4}\b/g,
  // Standard formats: (555) 123-4567, 555-123-4567, 555.123.4567
  /\(\d{3}\)[\s\-.]?\d{3}[\s\-.]?\d{4}/g,
  // International: +1-555-123-4567, +44 20 1234 5678
  /\+\d{1,3}[\s\-]?\d{1,4}[\s\-]?\d{1,4}[\s\-]?\d{1,9}/g,
  // Informal: call me at 5551234567
  /(?:call|text|phone|tel|mobile|cell)[\s\w]*\d{7,15}/gi,
  // 10+ consecutive digits that could be phone numbers
  /\b\d{10,15}\b/g,
];

// Email patterns
const EMAIL_PATTERNS = [
  // Standard email format
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  // Obfuscated: user at domain dot com
  /\b\w+\s+at\s+\w+\s+dot\s+\w+/gi,
  // Obfuscated: user[at]domain[dot]com
  /\b\w+\[at\]\w+\[dot\]\w+/gi,
  // Common email domains mentioned
  /\b\w+@?(gmail|yahoo|hotmail|outlook|aol|icloud)\.com\b/gi,
];

// Social media and messaging patterns
const SOCIAL_MEDIA_PATTERNS = [
  // Social handles: @username
  /@\w{3,30}/g,
  // WhatsApp mentions
  /whatsapp|whats\s*app/gi,
  // Other messaging apps
  /telegram|signal|snapchat|instagram|facebook|linkedin|twitter|tiktok/gi,
  // Social media URLs
  /(facebook|instagram|linkedin|twitter|tiktok)\.com\/[\w\.-]+/gi,
  // Skype mentions
  /skype|add\s+me\s+on\s+skype/gi,
];

// Website URL patterns
const URL_PATTERNS = [
  // Standard URLs
  /https?:\/\/[^\s]+/gi,
  // www.domain.com
  /www\.[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi,
  // domain.com patterns
  /\b[a-zA-Z0-9.-]+\.(com|net|org|edu|gov|co\.uk|io|me|us)\b/gi,
];

// Whitelist patterns for legitimate business content
const WHITELIST_PATTERNS = [
  // Room numbers, building numbers
  /room\s+\d+|building\s+\d+|floor\s+\d+/gi,
  // Invoice numbers, order numbers
  /invoice\s*#?\d+|order\s*#?\d+|ref\s*#?\d+/gi,
  // Version numbers
  /version\s+\d+\.\d+(\.\d+)?|v\d+\.\d+(\.\d+)?/gi,
  // Model numbers, part numbers
  /model\s+[\w\-]+|part\s+number\s+[\w\-]+/gi,
  // Time references
  /\d{1,2}:\d{2}(\s*(am|pm))?|\d{1,2}\s*(am|pm)/gi,
  // 24/7, 9-to-5 references
  /24\/7|9[\-\s]to[\-\s]5|business\s+hours/gi,
  // File extensions and technical terms
  /\.(pdf|doc|docx|xlsx|jpg|png|zip|txt)/gi,
];

function isWhitelistedContent(text: string): boolean {
  return WHITELIST_PATTERNS.some(pattern => pattern.test(text));
}

function detectPhoneNumbers(text: string): string[] {
  const found: string[] = [];
  
  PHONE_PATTERNS.forEach(pattern => {
    const matches = text.match(pattern) || [];
    matches.forEach(match => {
      // Filter out obviously non-phone patterns
      const cleanMatch = match.replace(/\D/g, '');
      
      // Must be 7-15 digits to be a potential phone number
      if (cleanMatch.length >= 7 && cleanMatch.length <= 15) {
        // Check if it's not whitelisted content
        if (!isWhitelistedContent(match)) {
          found.push(match);
        }
      }
    });
  });
  
  return [...new Set(found)]; // Remove duplicates
}

function detectEmails(text: string): string[] {
  const found: string[] = [];
  
  EMAIL_PATTERNS.forEach(pattern => {
    const matches = text.match(pattern) || [];
    found.push(...matches);
  });
  
  return [...new Set(found)];
}

function detectSocialMedia(text: string): string[] {
  const found: string[] = [];
  
  SOCIAL_MEDIA_PATTERNS.forEach(pattern => {
    const matches = text.match(pattern) || [];
    found.push(...matches);
  });
  
  return [...new Set(found)];
}

function detectUrls(text: string): string[] {
  const found: string[] = [];
  
  URL_PATTERNS.forEach(pattern => {
    const matches = text.match(pattern) || [];
    matches.forEach(match => {
      // Allow certain whitelisted domains or patterns
      if (!isWhitelistedContent(match)) {
        found.push(match);
      }
    });
  });
  
  return [...new Set(found)];
}

export function validateMessageContent(message: string): ContactInfoDetection {
  if (!message || typeof message !== 'string') {
    return { isValid: true };
  }

  const trimmedMessage = message.trim();
  
  // Check for phone numbers
  const phoneNumbers = detectPhoneNumbers(trimmedMessage);
  if (phoneNumbers.length > 0) {
    return {
      isValid: false,
      detectedType: 'phone_number',
      flaggedContent: phoneNumbers[0],
      message: 'Messages cannot contain phone numbers. Please use the platform messaging system for all communication.'
    };
  }

  // Check for email addresses
  const emails = detectEmails(trimmedMessage);
  if (emails.length > 0) {
    return {
      isValid: false,
      detectedType: 'email',
      flaggedContent: emails[0],
      message: 'Messages cannot contain email addresses. Please use the platform messaging system for all communication.'
    };
  }

  // Check for social media/messaging references
  const socialMedia = detectSocialMedia(trimmedMessage);
  if (socialMedia.length > 0) {
    return {
      isValid: false,
      detectedType: 'social_media',
      flaggedContent: socialMedia[0],
      message: 'Messages cannot contain social media or external messaging app references. Please keep all communication within the platform.'
    };
  }

  // Check for URLs
  const urls = detectUrls(trimmedMessage);
  if (urls.length > 0) {
    return {
      isValid: false,
      detectedType: 'url',
      flaggedContent: urls[0],
      message: 'Messages cannot contain website URLs. Please use the file upload system to share documents or references.'
    };
  }

  return { isValid: true };
}

// Export error types for consistent error handling
export const CONTACT_INFO_ERROR_TYPES = {
  PHONE_NUMBER: 'MESSAGE_CONTAINS_PHONE_NUMBER',
  EMAIL: 'MESSAGE_CONTAINS_EMAIL',
  SOCIAL_MEDIA: 'MESSAGE_CONTAINS_SOCIAL_MEDIA',
  URL: 'MESSAGE_CONTAINS_URL',
  CONTACT_INFO: 'MESSAGE_CONTAINS_CONTACT_INFO'
} as const;
